import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import {
  DollarSign,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Crown,
  Receipt,
  XCircle,
  AlertCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { cn } from "@/lib/utils";
import { eachWeekOfInterval, endOfWeek } from "date-fns";
import type { ReportDateRange } from "./ReportDateRangePicker";
import MatchiSettledRow from "@/components/admin/MatchiSettledRow";

// ---------------------------------------------------------------------------
// Record types
// ---------------------------------------------------------------------------

// Sprint D Plan A: dual-state types (legacy + _minor) on all money fields.
interface BookingRecord {
  id: string;
  status: string;
  user_id: string;
  start_time: string;
  duration_minutes: number | null;
  price_total: number | null;
  price_total_minor: number | null;
  currency: string;
  [key: string]: unknown;
}

interface PaymentRecord {
  amount: number;
  amount_minor: number | null;
  currency: string;
  paid_at: string | null;
  created_at: string;
  [key: string]: unknown;
}

interface MembershipRecord {
  id: string;
  status: string;
  plan_price: number | null;
  plan_price_minor: number | null;
  plan_currency: string;
  plan_interval: string | null;
  [key: string]: unknown;
}

interface NoShowRecord {
  id: string;
  created_at: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  range: ReportDateRange;
  bookings: BookingRecord[];
  prevBookings: BookingRecord[];
  payments: PaymentRecord[];
  prevPayments: PaymentRecord[];
  /** C3: MATCHi-avräknade betalningar — redan bortdragna ur `payments`. */
  matchiPayments?: PaymentRecord[];
  memberships: MembershipRecord[];
  noShows: NoShowRecord[];
  courtCount: number;
  hallHours: { start: number; end: number } | null;
  compareEnabled: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const INTERVAL_DIVISOR: Record<string, number> = {
  month: 1,
  quarter: 3,
  year: 12,
};

function daysInRange(range: ReportDateRange): number {
  const diff =
    (range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24);
  return Math.max(Math.round(diff) + 1, 1);
}

function confirmedBookings(bookings: BookingRecord[]): BookingRecord[] {
  return bookings.filter((b) => b.status !== "cancelled");
}

function pctDelta(current: number, previous: number): number {
  if (previous === 0) return current === 0 ? 0 : 100;
  return Math.round(((current - previous) / Math.abs(previous)) * 100);
}

function buildWeeklyBuckets<T>(
  items: T[],
  range: ReportDateRange,
  dateAccessor: (item: T) => string | null,
  valueAccessor: (item: T) => number,
): { v: number }[] {
  if (range.from >= range.to) return [];
  const weeks = eachWeekOfInterval(
    { start: range.from, end: range.to },
    { weekStartsOn: 1 },
  );
  if (weeks.length === 0) return [];

  const buckets = weeks.map((weekStart) => ({
    weekStart,
    weekEnd: endOfWeek(weekStart, { weekStartsOn: 1 }),
    v: 0,
  }));

  for (const item of items) {
    const raw = dateAccessor(item);
    if (!raw) continue;
    const d = new Date(raw);
    for (const bucket of buckets) {
      if (d >= bucket.weekStart && d <= bucket.weekEnd) {
        bucket.v += valueAccessor(item);
        break;
      }
    }
  }

  return buckets.map(({ v }) => ({ v }));
}

// ---------------------------------------------------------------------------
// KPI card definition
// ---------------------------------------------------------------------------

interface KpiDef {
  label: string;
  icon: LucideIcon;
  value: number;
  formatted: string;
  prevValue: number | null;
  sparkData: { v: number }[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DashboardTab({
  range,
  bookings,
  prevBookings,
  payments,
  prevPayments,
  matchiPayments = [],
  memberships,
  noShows,
  courtCount,
  hallHours,
  compareEnabled,
}: Props) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const hoursPerDay = hallHours ? hallHours.end - hallHours.start : 0;
  const days = daysInRange(range);

  const confirmed = useMemo(() => confirmedBookings(bookings), [bookings]);
  const prevConfirmed = useMemo(
    () => confirmedBookings(prevBookings),
    [prevBookings],
  );

  // ---- Revenue (Omsattning) — Sprint D aggregates: prefer amount_minor.
  const revenueMinor = useMemo(
    () => payments.reduce((s, p) => s + (resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0), 0),
    [payments],
  );
  const prevRevenueMinor = useMemo(
    () => prevPayments.reduce((s, p) => s + (resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0), 0),
    [prevPayments],
  );
  // C3/BES-004: särredovisat, aldrig inbakat i revenueMinor ovan.
  const matchiSettledMinor = useMemo(
    () => matchiPayments.reduce((s, p) => s + (resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0), 0),
    [matchiPayments],
  );
  // Backwards-compat aliases (kronor) for downstream comparisons + sparkline display.
  const revenue = Math.round(revenueMinor / 100);
  const prevRevenue = Math.round(prevRevenueMinor / 100);
  const revenueSparkData = useMemo(
    () =>
      buildWeeklyBuckets(
        payments,
        range,
        (p) => p.paid_at ?? p.created_at,
        (p) => Math.round((resolveMoney(p.amount_minor, p.amount, p.currency)?.valueMinor ?? 0) / 100),
      ),
    [payments, range],
  );

  // ---- Occupancy (Beläggning) -----------------------------------------------
  const totalBookedHours = useMemo(
    () => confirmed.reduce((s, b) => s + (b.duration_minutes ?? 0) / 60, 0),
    [confirmed],
  );
  const prevTotalBookedHours = useMemo(
    () =>
      prevConfirmed.reduce((s, b) => s + (b.duration_minutes ?? 0) / 60, 0),
    [prevConfirmed],
  );
  const totalCapacity = courtCount * hoursPerDay * days;
  const occupancy =
    totalCapacity > 0 ? (totalBookedHours / totalCapacity) * 100 : 0;
  const prevOccupancy =
    totalCapacity > 0 ? (prevTotalBookedHours / totalCapacity) * 100 : 0;
  const occupancySparkData = useMemo(
    () =>
      buildWeeklyBuckets(
        confirmed,
        range,
        (b) => b.start_time,
        (b) => (b.duration_minutes ?? 0) / 60,
      ),
    [confirmed, range],
  );

  // ---- RevPAH — Sprint D aggregates: prefer price_total_minor.
  const totalConfirmedRevenueMinor = useMemo(
    () => confirmed.reduce((s, b) => s + (resolveMoney(b.price_total_minor, b.price_total, b.currency)?.valueMinor ?? 0), 0),
    [confirmed],
  );
  const prevTotalConfirmedRevenueMinor = useMemo(
    () => prevConfirmed.reduce((s, b) => s + (resolveMoney(b.price_total_minor, b.price_total, b.currency)?.valueMinor ?? 0), 0),
    [prevConfirmed],
  );
  const totalConfirmedRevenue = Math.round(totalConfirmedRevenueMinor / 100);
  const prevTotalConfirmedRevenue = Math.round(prevTotalConfirmedRevenueMinor / 100);
  const revpah = totalCapacity > 0 ? totalConfirmedRevenue / totalCapacity : 0;
  const prevRevpah =
    totalCapacity > 0 ? prevTotalConfirmedRevenue / totalCapacity : 0;
  const revpahSparkData = useMemo(
    () =>
      buildWeeklyBuckets(
        confirmed,
        range,
        (b) => b.start_time,
        (b) => Math.round((resolveMoney(b.price_total_minor, b.price_total, b.currency)?.valueMinor ?? 0) / 100),
      ),
    [confirmed, range],
  );

  // ---- Active players (Aktiva spelare) --------------------------------------
  const activePlayers = useMemo(
    () => new Set(confirmed.map((b) => b.user_id)).size,
    [confirmed],
  );
  const prevActivePlayers = useMemo(
    () => new Set(prevConfirmed.map((b) => b.user_id)).size,
    [prevConfirmed],
  );
  const playersSparkData = useMemo(
    () =>
      buildWeeklyBuckets(
        confirmed,
        range,
        (b) => b.start_time,
        () => 1,
      ),
    [confirmed, range],
  );

  // ---- MRR — Sprint D aggregates: prefer plan_price_minor.
  const mrr = useMemo(
    () =>
      memberships
        .filter((m) => m.status === "active")
        .reduce((s, m) => {
          const priceMinor = resolveMoney(m.plan_price_minor, m.plan_price, m.plan_currency)?.valueMinor ?? 0;
          const divisor = INTERVAL_DIVISOR[m.plan_interval ?? "month"] ?? 1;
          return s + priceMinor / divisor;
        }, 0) / 100,
    [memberships],
  );

  // ---- Average per booking (Snitt/bokning) ----------------------------------
  const avgPerBooking =
    confirmed.length > 0 ? revenue / confirmed.length : 0;
  const prevAvgPerBooking =
    prevConfirmed.length > 0 ? prevRevenue / prevConfirmed.length : 0;
  const avgSparkData = revenueSparkData; // reuse revenue spark as proxy

  // ---- Cancellation rate (Avbokningsgrad) -----------------------------------
  const cancelled = useMemo(
    () => bookings.filter((b) => b.status === "cancelled"),
    [bookings],
  );
  const prevCancelled = useMemo(
    () => prevBookings.filter((b) => b.status === "cancelled"),
    [prevBookings],
  );
  const cancelRate =
    bookings.length > 0 ? (cancelled.length / bookings.length) * 100 : 0;
  const prevCancelRate =
    prevBookings.length > 0
      ? (prevCancelled.length / prevBookings.length) * 100
      : 0;
  const cancelSparkData = useMemo(
    () =>
      buildWeeklyBuckets(
        cancelled,
        range,
        (b) => b.start_time,
        () => 1,
      ),
    [cancelled, range],
  );

  // ---- No-show rate ---------------------------------------------------------
  const noShowsInRange = useMemo(
    () =>
      noShows.filter((n) => {
        const d = new Date(n.created_at);
        return d >= range.from && d <= range.to;
      }),
    [noShows, range],
  );
  const noShowRate =
    confirmed.length > 0
      ? (noShowsInRange.length / confirmed.length) * 100
      : 0;
  // No prev comparison for no-show in the same way — use 0
  const noShowSparkData = useMemo(
    () =>
      buildWeeklyBuckets(
        noShowsInRange,
        range,
        (n) => n.created_at,
        () => 1,
      ),
    [noShowsInRange, range],
  );

  // ---- Build KPI definitions ------------------------------------------------
  const kpis: KpiDef[] = useMemo(
    () => [
      {
        label: t("reports.dashboard.revenue"),
        icon: DollarSign,
        value: revenue,
        formatted: formatPrice(revenue),
        prevValue: prevRevenue,
        sparkData: revenueSparkData,
      },
      {
        label: t("reports.dashboard.occupancy"),
        icon: BarChart3,
        value: occupancy,
        formatted: `${Math.round(occupancy)}%`,
        prevValue: prevOccupancy,
        sparkData: occupancySparkData,
      },
      {
        label: "RevPAH",
        icon: TrendingUp,
        value: revpah,
        formatted: formatPrice(revpah),
        prevValue: prevRevpah,
        sparkData: revpahSparkData,
      },
      {
        label: t("reports.dashboard.activePlayers"),
        icon: Users,
        value: activePlayers,
        formatted: String(activePlayers),
        prevValue: prevActivePlayers,
        sparkData: playersSparkData,
      },
      {
        label: "MRR",
        icon: Crown,
        value: mrr,
        formatted: formatPrice(mrr),
        prevValue: null,
        sparkData: [],
      },
      {
        label: t("reports.dashboard.avgPerBooking"),
        icon: Receipt,
        value: avgPerBooking,
        formatted: formatPrice(avgPerBooking),
        prevValue: prevAvgPerBooking,
        sparkData: avgSparkData,
      },
      {
        label: t("reports.dashboard.cancellationRate"),
        icon: XCircle,
        value: cancelRate,
        formatted: `${Math.round(cancelRate * 10) / 10}%`,
        prevValue: prevCancelRate,
        sparkData: cancelSparkData,
      },
      {
        label: "No-show",
        icon: AlertCircle,
        value: noShowRate,
        formatted: `${Math.round(noShowRate * 10) / 10}%`,
        prevValue: null,
        sparkData: noShowSparkData,
      },
    ],
    [
      revenue,
      prevRevenue,
      revenueSparkData,
      occupancy,
      prevOccupancy,
      occupancySparkData,
      revpah,
      prevRevpah,
      revpahSparkData,
      activePlayers,
      prevActivePlayers,
      playersSparkData,
      mrr,
      avgPerBooking,
      prevAvgPerBooking,
      avgSparkData,
      cancelRate,
      prevCancelRate,
      cancelSparkData,
      noShowRate,
      noShowSparkData,
    ],
  );

  // ---- Budget progress widget ------------------------------------------------
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const { data: budgetEntries = [] } = useQuery({
    queryKey: ["dashboard-budget", organization?.id, currentYear, currentMonth],
    queryFn: async () => {
      // Sprint D aggregates: include amount_minor + currency.
      const { data } = await supabase
        .from("budget_entries" as any)
        .select("amount, amount_minor, currency")
        .eq("organization_id", organization!.id)
        .eq("year", currentYear)
        .eq("month", currentMonth);
      return (data ?? []) as { amount: number; amount_minor: number | null; currency: string }[];
    },
    enabled: !!organization?.id,
  });

  const monthBudgetMinor = budgetEntries.reduce(
    (s, e) => s + (resolveMoney(e.amount_minor, e.amount, e.currency)?.valueMinor ?? 0), 0);
  const monthBudget = Math.round(monthBudgetMinor / 100);
  const budgetPct = monthBudget > 0 ? Math.min(Math.round((revenue / monthBudget) * 100), 100) : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((kpi) => {
          const delta =
            kpi.prevValue != null ? pctDelta(kpi.value, kpi.prevValue) : null;

          return (
            <Card key={kpi.label}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  <kpi.icon className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold">{kpi.formatted}</p>

                {/* Sparkline */}
                {kpi.sparkData.length > 1 && (
                  <div className="mt-1">
                    <ResponsiveContainer width={80} height={32}>
                      <LineChart data={kpi.sparkData}>
                        <Line
                          type="monotone"
                          dataKey="v"
                          stroke="hsl(var(--primary))"
                          strokeWidth={1.5}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Comparison delta */}
                {compareEnabled && delta != null && (
                  <div className="flex items-center gap-1 mt-1">
                    {delta >= 0 ? (
                      <TrendingUp className="h-3 w-3 text-green-500" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-destructive" />
                    )}
                    <span
                      className={cn(
                        "text-xs font-medium",
                        delta >= 0 ? "text-green-500" : "text-destructive",
                      )}
                    >
                      {delta >= 0 ? "+" : ""}
                      {delta}%
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* C3/BES-004: MATCHi-avräknat — utanför KPI:n "Omsättning" ovan. */}
      <MatchiSettledRow
        valueMinor={matchiSettledMinor}
        className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
      />

      {/* Budget progress widget */}
      {monthBudget > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">
                {t("reports.dashboard.budgetFulfillment")}
              </p>
              <span className="text-xs font-medium">{budgetPct}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  budgetPct >= 90 ? "bg-green-500" : budgetPct >= 60 ? "bg-primary" : "bg-orange-400",
                )}
                style={{ width: `${budgetPct}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>{t("reports.dashboard.actual")}: {formatPrice(revenue)}</span>
              <span>{t("reports.dashboard.budget")}: {formatPrice(monthBudget)}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
